import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import PageForm from '../../templates/PageForm';
import Dialog from '../../organisms/Dialog';
import SelectFilter from '../../organisms/SelectFilter';
import CopyPasteTable from '../../organisms/CopyPasteTable';
import DuplicateIssuanceModal from './DuplicateIssuanceModal';
import { useDuplicateConfirmation } from './useDuplicateConfirmation';
import TextEdit from '../../molecules/TextEdit';
import NumberEdit from '../../molecules/NumberEdit';
import CurrencyInput from '../../molecules/CurrencyInput';
import { AuthContext } from '../../../services/Auth/AuthContext';
import { fundingApi, CreateInstrumentItem, InstrumentProduct, Platform, PuDesagiadoPreviewItem, RateType } from '../../../services/fundingApi';
import { extractAndConvertFrom } from '../../../utils/DateUtils';
import {
    BROKER_FIELD_MAPPING,
    ISSUANCE_HEADERS,
    PastedRow,
    platformCetipKey,
    toCreateItem,
    todayIso,
} from './issuanceMapping';

interface Form {
    platformId: string;
    issuances?: PastedRow[];
}

const VALID_PRODUCTS: InstrumentProduct[] = ['LCA', 'CDB', 'DI'];
const VALID_RATE_TYPES: RateType[] = ['PRE', 'POS'];

// DI não tem PU Desagiado (liquida direto com a contraparte via CETIP) e
// pode ter um spread aditivo sobre o indexador (ex: "100% CDI + 1,85% Pré") -
// ver docs/context/business-context.md secao 8.2.
const requiresPuDegassed = (product: InstrumentProduct | ''): boolean => product !== 'DI';

const PRODUCT_SELECT_OPTIONS = VALID_PRODUCTS.map((p) => ({ value: p, label: p }));
// Rótulo exibido é "PRÉ"/"%CDI" (pedido do negócio, 2026-09-10) - o value
// enviado pra API continua 'PRE'/'POS', só a label muda.
const RATE_TYPE_LABELS: Record<RateType, string> = { PRE: 'PRÉ', POS: '%CDI' };
const RATE_TYPE_SELECT_OPTIONS = VALID_RATE_TYPES.map((r) => ({ value: r, label: RATE_TYPE_LABELS[r] }));
// PU é sempre 1.000 (convenção de mercado pra LCA/CDB/DI) - travado na
// emissão manual desde 2026-09-10, não é mais um valor digitável.
const FIXED_PU = 1000;
// PU Desagiado é digitado com 3 dígitos inteiros e 8 casas (ex: 999,99999999),
// mesma precisão da listagem. Com 2 casas, a máscara do CurrencyInput (dígitos
// entram pela direita) transformava "998,21650200" em 9.982.165,02 - bug real
// de produção, 2026-10-09.
const PU_DEGASSED_FRACTION_DIGITS = 8;
const PU_DEGASSED_MAX = 999.99999999;

// Validações de um único item, sem prefixo de linha - usado tanto pelo modo
// de colagem (que prefixa "Linha N:") quanto pelo cadastro manual (item único).
const itemProblems = (item: CreateInstrumentItem): string[] => {
    const problems: string[] = [];

    if (!VALID_PRODUCTS.includes(item.product)) {
        problems.push(`produto "${item.product || '(vazio)'}" inválido - aceito apenas LCA, CDB ou DI.`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(item.issue_date)) {
        problems.push(`data de emissão "${item.issue_date || '(vazio)'}" fora do formato esperado.`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(item.due_date)) {
        problems.push(`vencimento "${item.due_date || '(vazio)'}" fora do formato esperado (DD/MM/AAAA).`);
    }
    if (!VALID_RATE_TYPES.includes(item.rate_type)) {
        problems.push('indexador (PRE/POS) inválido.');
    }
    if (item.rate_type === 'POS' && !item.remuneration_index) {
        problems.push('índice não informado (obrigatório quando o indexador é POS).');
    }
    if (!item.quantity || item.quantity <= 0 || !Number.isInteger(item.quantity)) {
        problems.push('quantidade inválida (deve ser um número inteiro maior que zero).');
    }
    if (!item.tax || item.tax <= 0) {
        problems.push('taxa inválida.');
    }
    if (!item.pu || item.pu <= 0) {
        problems.push('PU inválido.');
    }
    if (requiresPuDegassed(item.product) && (!item.pu_degassed || item.pu_degassed <= 0)) {
        problems.push('PU desagiado inválido.');
    }
    if (!item.financial_value || item.financial_value <= 0) {
        problems.push('valor inválido.');
    }

    return problems;
};

const validateItems = (items: CreateInstrumentItem[], platformId: string): string[] => {
    const problems: string[] = [];

    if (!platformId) {
        problems.push('Selecione a corretora/plataforma.');
    }

    items.forEach((item, idx) => {
        itemProblems(item).forEach((p) => problems.push(`Linha ${idx + 1}: ${p}`));
    });

    return problems;
};

// financialValue não é mais um campo digitado - "Valor Emissão" (QNTDxPU) e
// "Valor Recebido" (QNTDxPU Desagiado) são sempre calculados, nunca
// manipuláveis (pedido do negócio, 2026-09-10).
type ManualEntry = {
    product: InstrumentProduct | '';
    issueDate: string;
    dueDate: string;
    rateType: RateType;
    remunerationIndex: string;
    tax: number;
    quantity: number;
    pu: number;
    puDegassed: number;
    diFuturo: number;
    additionalSpread: number;
};

const EMPTY_MANUAL_ENTRY: ManualEntry = {
    product: '',
    issueDate: '',
    dueDate: '',
    rateType: 'PRE',
    remunerationIndex: '',
    tax: 0,
    quantity: 0,
    pu: FIXED_PU,
    puDegassed: 0,
    diFuturo: 0,
    additionalSpread: 0,
};

const IssuanceFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const form = useSignal<Form>({ platformId: '', issuances: [] });
    const isSending = useSignal(false);
    const buttonDisabled = useSignal(true);
    const hasErrors = useSignal(false);
    const currentMapping = useSignal<Record<string, string>>({});

    const platforms = useSignal<Platform[]>([]);
    const isLoadingPlatforms = useSignal(false);

    const entryMode = useSignal<'paste' | 'manual'>('paste');
    const manualEntry = useSignal<ManualEntry>(EMPTY_MANUAL_ENTRY);
    const manualItems = useSignal<CreateInstrumentItem[]>([]);

    // Conferência de PU Desagiado/Taxa Cheia ANTES de salvar - achado real em
    // 2026-09-09/10: boletas do Daycoval com PU errado na planilha (a fórmula
    // acertou o valor certo), e a emissão manual nunca tem taxa cheia
    // informada pra começo de conversa. Desde 2026-09-10 o modal abre SEMPRE
    // antes de salvar (manual ou colagem), não só quando diverge - o negócio
    // quer conferir a taxa cheia calculada como rotina, não só quando dá
    // problema. O dado da corretora continua sendo o oficial - o usuário
    // decide se envia mesmo assim. `pendingDivergenceDecision` guarda o
    // resolve() da Promise que `submitForm` está aguardando enquanto o modal
    // fica aberto, já que o modal em si é declarativo (não bloqueia como
    // window.confirm bloqueava).
    type PreviewRow = {
        index: number;
        informedTaxGross: number | null;
        calculatedTaxGross: number | null;
        diverges: boolean;
        relativeDifference: number | null;
    };
    const duplicates = useDuplicateConfirmation();
    const divergenceModal = useSignal<{ show: boolean; rows: PreviewRow[] }>({ show: false, rows: [] });
    const pendingDivergenceDecision = useRef<((proceed: boolean) => void) | null>(null);

    const confirmPuDesagiadoDivergences = async (items: CreateInstrumentItem[]): Promise<boolean> => {
        let preview: PuDesagiadoPreviewItem[];
        try {
            const result = await fundingApi.previewPuDesagiado(items);
            // Se a própria conferência falhar (rede, backend fora, etc.), deixa
            // passar: é um alerta, não pode virar um novo motivo de travar o
            // cadastro (ver bug de congelamento corrigido no mesmo dia).
            if (!result.ok) return true;
            preview = result.data;
        } catch {
            return true;
        }

        // Só mostra linha pra quem tem alguma informação útil pra conferir
        // (taxa cheia calculada, ou divergência real de PU Desagiado) - DI e
        // afins (sem PU Desagiado) não têm nada a conferir aqui.
        const rows: PreviewRow[] = preview
            .filter((p) => p.impliedTaxGross != null || (p.applicable && p.diverges))
            .map((p) => ({
                index: p.index,
                informedTaxGross: items[p.index]?.tax_gross ?? null,
                calculatedTaxGross: p.impliedTaxGross != null ? Number(p.impliedTaxGross) : null,
                diverges: !!(p.applicable && p.diverges),
                relativeDifference: p.relativeDifference != null ? Number(p.relativeDifference) : null,
            }));
        if (rows.length === 0) return true;

        return new Promise<boolean>((resolve) => {
            pendingDivergenceDecision.current = resolve;
            divergenceModal.value = { show: true, rows };
        });
    };

    const resolveDivergenceModal = (proceed: boolean) => {
        divergenceModal.value = { show: false, rows: [] };
        pendingDivergenceDecision.current?.(proceed);
        pendingDivergenceDecision.current = null;
    };

    // Taxa Cheia calculada ao vivo na emissão manual - a corretora não
    // informa taxa cheia num cadastro manual, então back-calcula a partir do
    // PU Desagiado/Taxa Cliente/prazo digitados (pedido do negócio,
    // 2026-09-10). Debounce de 500ms pra não bater na API a cada tecla; os
    // campos não usados pelo cálculo (quantidade, valor, plataforma) são só
    // placeholders - fundingApi.previewPuDesagiado descarta tudo que não for
    // product/rate_type/datas/tax/tax_gross/pu/pu_degassed/di_futuro.
    const manualImpliedTaxGross = useSignal<number | null>(null);
    useEffect(() => {
        const entry = manualEntry.value;
        const ready = isManualMode()
            && requiresPuDegassed(entry.product)
            && entry.puDegassed > 0
            && entry.tax > 0
            && !!entry.dueDate
            && (entry.rateType !== 'POS' || entry.diFuturo > 0);

        if (!ready) {
            manualImpliedTaxGross.value = null;
            return;
        }

        const timer = setTimeout(async () => {
            const previewItem: CreateInstrumentItem = {
                product: entry.product as InstrumentProduct,
                issue_date: entry.issueDate || todayIso(),
                due_date: entry.dueDate,
                rate_type: entry.rateType,
                remuneration_index: entry.rateType === 'POS' ? (entry.remunerationIndex || 'CDI') : null,
                tax: entry.tax,
                quantity: 1,
                pu: entry.pu,
                pu_degassed: entry.puDegassed,
                financial_value: entry.pu,
                di_futuro: entry.rateType === 'POS' ? entry.diFuturo : null,
                additional_spread: null,
                platform_id: form.value.platformId,
            };
            // Preview "ao vivo" - falha de rede/token não pode virar unhandled
            // rejection nem travar nada visível, só deixa a Taxa Cheia
            // calculada sem valor (o usuário pode continuar digitando).
            try {
                const result = await fundingApi.previewPuDesagiado([previewItem]);
                const implied = result.ok ? result.data[0]?.impliedTaxGross : undefined;
                manualImpliedTaxGross.value = implied != null ? Number(implied) : null;
            } catch {
                manualImpliedTaxGross.value = null;
            }
        }, 500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        entryMode.value,
        manualEntry.value.product,
        manualEntry.value.rateType,
        manualEntry.value.tax,
        manualEntry.value.puDegassed,
        manualEntry.value.pu,
        manualEntry.value.issueDate,
        manualEntry.value.dueDate,
        manualEntry.value.diFuturo,
    ]);

    useEffect(() => {
        const loadPlatforms = async () => {
            isLoadingPlatforms.value = true;
            // try/finally: listPlatforms pode rejeitar (falha de rede/refresh
            // de token via tokenAwareFetcher), não só resolver com ok:false -
            // sem isso a rejeição ficava sem handler e o loading travava.
            try {
                const result = await fundingApi.listPlatforms();
                if (!result.ok) {
                    toast.error(`Não foi possível carregar as corretoras: ${result.message}`);
                    return;
                }

                platforms.value = result.data.filter((p) => p.active);
            } catch (error) {
                toast.error(`Não foi possível carregar as corretoras: ${error instanceof Error ? error.message : 'erro inesperado'}.`);
            } finally {
                isLoadingPlatforms.value = false;
            }
        };
        loadPlatforms();
    }, []);

    useEffect(() => {
        const handleJson = (event: CustomEvent) => {
            const data = event.detail as PastedRow[];
            form.value = { ...form.value, issuances: data };
            buttonDisabled.value = hasErrors.value || data.length === 0 || !form.value.platformId;
        };
        window.addEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
        return () => window.removeEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
    }, []);

    useEffect(() => {
        const handleErrors = (event: CustomEvent) => {
            const errors = event.detail as unknown[];
            hasErrors.value = errors.length > 0;
            buttonDisabled.value = hasErrors.value || (form.value.issuances?.length || 0) === 0 || !form.value.platformId;
        };
        window.addEventListener('CopyPasteTable:errors', handleErrors as EventListener);
        return () => window.removeEventListener('CopyPasteTable:errors', handleErrors as EventListener);
    }, [form.value.issuances?.length]);

    const clearData = () => {
        window.dispatchEvent(new Event('CopyPasteTable:clearData'));
    };

    const selectPlatform = (value: string) => {
        const platform = platforms.value.find((p) => p.id === value);
        if (!platform) return;

        form.value = { ...form.value, platformId: value };
        currentMapping.value = BROKER_FIELD_MAPPING[platformCetipKey(platform)] || {};
        clearData();
        manualItems.value = [];
    };

    // Trocar de modo descarta os dados do modo anterior - evita o cenário de
    // colar 5 linhas, trocar pra manual, adicionar 1 e enviar só essa 1 sem
    // o usuário perceber que as outras 5 ficaram de fora do envio.
    const switchMode = (mode: 'paste' | 'manual') => {
        if (mode === entryMode.value) return;
        entryMode.value = mode;
        clearData();
        manualItems.value = [];
        manualEntry.value = EMPTY_MANUAL_ENTRY;
    };

    const addManualItem = () => {
        if (!form.value.platformId) {
            toast.error('Selecione a corretora/plataforma antes de adicionar.');
            return;
        }

        const item: CreateInstrumentItem = {
            product: manualEntry.value.product as InstrumentProduct,
            issue_date: manualEntry.value.issueDate || todayIso(),
            due_date: manualEntry.value.dueDate,
            rate_type: manualEntry.value.rateType,
            remuneration_index: manualEntry.value.rateType === 'POS' ? manualEntry.value.remunerationIndex.trim().toUpperCase() : null,
            tax: manualEntry.value.tax,
            quantity: Math.round(manualEntry.value.quantity),
            pu: manualEntry.value.pu,
            pu_degassed: requiresPuDegassed(manualEntry.value.product) ? manualEntry.value.puDegassed : null,
            financial_value: Math.round(manualEntry.value.quantity) * manualEntry.value.pu,
            di_futuro: manualEntry.value.rateType === 'POS' && manualEntry.value.diFuturo ? manualEntry.value.diFuturo : null,
            additional_spread: manualEntry.value.product === 'DI' && manualEntry.value.additionalSpread ? manualEntry.value.additionalSpread : null,
            platform_id: form.value.platformId,
        };

        const problems = itemProblems(item);
        if (problems.length > 0) {
            problems.forEach((p) => toast.error(p));
            return;
        }

        manualItems.value = [...manualItems.value, item];
        manualEntry.value = EMPTY_MANUAL_ENTRY;
    };

    const removeManualItem = (idx: number) => {
        manualItems.value = manualItems.value.filter((_, i) => i !== idx);
    };

    const isManualMode = () => entryMode.value === 'manual';

    const saveDisabled = isManualMode()
        ? (isSending.value || !form.value.platformId || manualItems.value.length === 0)
        : (isSending.value || buttonDisabled.value);

    const submitForm = async () => {
        // Lock adquirido ANTES de qualquer await (inclusive o preview de PU
        // Desagiado) e liberado em todo caminho de saída, sucesso ou erro.
        // Bug real: antes disso só era travado depois do preview/confirmação -
        // dois cliques rápidos no Salvar passavam os dois pela mesma janela
        // (preview aguardando API) e podiam disparar dois POST /instruments,
        // duplicando a emissão. `isSending.value` é lido fresco (signal), não
        // uma variável capturada no closure do render - por isso o guard
        // funciona mesmo entre cliques quase simultâneos.
        if (isSending.value || saveDisabled) {
            if (saveDisabled && !isSending.value) {
                toast.error('Por favor, corrija os erros antes de enviar o formulário.');
            }
            return;
        }
        isSending.value = true;

        try {
            if (!user?.email) {
                toast.error('Não foi possível identificar o usuário logado.');
                return;
            }

            const platformId = form.value.platformId;
            const platform = platforms.value.find((p) => p.id === platformId);
            const platformCetip = platform ? platformCetipKey(platform) : '';
            const items = isManualMode()
                ? manualItems.value
                : (form.value.issuances || []).map((row) => toCreateItem(row, platformId, platformCetip));
            const problems = validateItems(items, platformId);
            if (problems.length > 0) {
                problems.slice(0, 5).forEach((message) => toast.error(message));
                return;
            }

            if (!(await duplicates.confirm(items))) {
                return;
            }

            if (!(await confirmPuDesagiadoDivergences(items))) {
                return;
            }

            const result = await fundingApi.createInstruments(items, user.username);

            if (!result.ok) {
                if (result.errors.length > 0) {
                    result.errors.slice(0, 5).forEach((err) => toast.error(`${err.fieldName}: ${err.message}`));
                } else {
                    toast.error(result.message);
                }
                return;
            }

            toast.info(`${result.data.createdIds.length} emissão(ões) lançada(s) com sucesso.`);
            navigate('/funding/issuance/list');
        } catch (error) {
            toast.error(
                `Falha ao enviar as emissões: ${error instanceof Error ? error.message : 'erro inesperado'}. Nenhuma emissão foi lançada - confira os dados e tente de novo.`,
            );
        } finally {
            isSending.value = false;
        }
    };

    const cancelForm = () => navigate(-1);

    // Conta CETIP é por produto desde 2026-08-19 (Daycoval usa contas diferentes
    // para LCA e CDB) - mostra as duas só quando divergem, senão só uma. DI
    // (ex: SICOOB, ITAU UNIBANCO) só tem cetipAccountDi - LCA/CDB ficam null.
    const formatCetipLabel = (p: Platform): string => {
        if (p.cetipAccountLca && p.cetipAccountCdb && p.cetipAccountLca !== p.cetipAccountCdb) {
            return `CETIP LCA ${p.cetipAccountLca} / CDB ${p.cetipAccountCdb}`;
        }
        const account = p.cetipAccountLca || p.cetipAccountCdb || p.cetipAccountDi;
        return account ? `CETIP ${account}` : 'sem conta CETIP cadastrada';
    };

    const platformOptions = platforms.value.map((p) => ({
        value: p.id,
        label: `${p.name} — ${formatCetipLabel(p)}`,
    }));

    const hasKnownMapping = Object.keys(currentMapping.value).length > 0;

    return (
        <>
        <PageForm
            pageTitle='Lançar Emissões'
            apiVersion='Funding API'
            isLoading={false}
            breadcrumb={[{ label: 'Tesouraria', url: '/funding/issuance/list' }, { label: 'Lançar Emissões', url: '#' }]}
            controlSlot={(
                <div className='flex space-x-4 justify-end m-2'>
                    {!isManualMode() && form.value.issuances && form.value.issuances.length > 0 && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800'
                            onClick={clearData}
                            disabled={isSending.value}
                        >
                            Limpar Dados
                        </button>
                    )}
                    {isManualMode() && manualItems.value.length > 0 && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800'
                            onClick={() => { manualItems.value = []; }}
                            disabled={isSending.value}
                        >
                            Limpar Itens
                        </button>
                    )}
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                        onClick={cancelForm}
                        disabled={isSending.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700'
                        onClick={submitForm}
                        disabled={saveDisabled}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <div className='container mx-auto p-4 space-y-4'>
                <SelectFilter
                    id='slc-broker'
                    label='Corretora:'
                    value={form.value.platformId}
                    onChange={selectPlatform}
                    defaultVoidOption
                    defaultLabel={isLoadingPlatforms.value ? 'Carregando corretoras...' : 'Selecione a corretora...'}
                    options={platformOptions}
                />

                {form.value.platformId && (
                    <div className='flex gap-2 border-b border-gray-200 dark:border-gray-700'>
                        <button
                            type='button'
                            onClick={() => switchMode('paste')}
                            className={`px-4 py-2 text-sm border-b-2 -mb-px ${entryMode.value === 'paste'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-medium'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                        >
                            Colar planilha
                        </button>
                        <button
                            type='button'
                            onClick={() => switchMode('manual')}
                            className={`px-4 py-2 text-sm border-b-2 -mb-px ${entryMode.value === 'manual'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-medium'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                        >
                            Emissão manual
                        </button>
                    </div>
                )}

                {form.value.platformId && entryMode.value === 'paste' && !hasKnownMapping && (
                    <p className='text-sm text-amber-600 dark:text-amber-400'>
                        Não há mapeamento automático de colunas conhecido para esta corretora ainda -
                        cole os dados e arraste as colunas na ordem correta (Produto, Data Emissão, Vencimento,
                        Indexador, Taxa, Quantidade, PU, PU Desagiado, Valor, DI Futuro).
                    </p>
                )}

                {form.value.platformId && entryMode.value === 'paste' && (
                    <CopyPasteTable
                        fixedHeaders={ISSUANCE_HEADERS}
                        allowDuplicates={true}
                        // Sem mapeamento conhecido (ex: GENIAL INST), não passar
                        // columnMapping - um objeto vazio faria o CopyPasteTable tratar a
                        // 1ª linha colada como cabeçalho e zerar todos os campos (nenhuma
                        // coluna bateria). undefined cai no fallback manual (drag-and-drop).
                        columnMapping={hasKnownMapping ? currentMapping.value : undefined}
                    />
                )}

                {form.value.platformId && entryMode.value === 'manual' && (
                    <div className='space-y-4'>
                        <div className='grid grid-cols-3 gap-4'>
                            <SelectFilter
                                id='manual-product'
                                label='Produto'
                                value={manualEntry.value.product}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, product: v as InstrumentProduct }; }}
                                defaultVoidOption
                                defaultLabel='Selecione...'
                                options={PRODUCT_SELECT_OPTIONS}
                            />
                            <TextEdit
                                id='manual-issue-date'
                                label='Data Emissão'
                                type='date'
                                value={manualEntry.value.issueDate}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, issueDate: e.target.value }; }}
                                placeholder={todayIso()}
                            />
                            <TextEdit
                                id='manual-due-date'
                                label='Vencimento'
                                type='date'
                                value={manualEntry.value.dueDate}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, dueDate: e.target.value }; }}
                            />
                            <SelectFilter
                                id='manual-rate-type'
                                label='Indexador'
                                value={manualEntry.value.rateType}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, rateType: v as RateType }; }}
                                options={RATE_TYPE_SELECT_OPTIONS}
                            />
                            {manualEntry.value.rateType === 'POS' && (
                                <TextEdit
                                    id='manual-index'
                                    label='Índice (ex: CDI)'
                                    value={manualEntry.value.remunerationIndex}
                                    onChange={(e) => { manualEntry.value = { ...manualEntry.value, remunerationIndex: e.target.value }; }}
                                />
                            )}
                            <CurrencyInput
                                id='manual-tax'
                                label='Taxa (%)'
                                value={manualEntry.value.tax}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, tax: v }; }}
                                fractionDigits={2}
                            />
                            <NumberEdit
                                id='manual-quantity'
                                label='Quantidade'
                                value={manualEntry.value.quantity || ''}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, quantity: parseInt(e.target.value || '0', 10) }; }}
                            />
                            <CurrencyInput
                                id='manual-pu'
                                label='PU (R$)'
                                value={manualEntry.value.pu}
                                disabled
                                title='PU é sempre R$ 1.000,00 (convenção de mercado) - não editável'
                                fractionDigits={2}
                            />
                            {requiresPuDegassed(manualEntry.value.product) && (
                                <CurrencyInput
                                    id='manual-pu-degassed'
                                    label='PU Desagiado (R$)'
                                    value={manualEntry.value.puDegassed}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, puDegassed: v }; }}
                                    fractionDigits={PU_DEGASSED_FRACTION_DIGITS}
                                    maxValue={PU_DEGASSED_MAX}
                                />
                            )}
                            <CurrencyInput
                                id='manual-financial-value'
                                label='Valor Emissão (R$)'
                                value={Math.round(manualEntry.value.quantity) * manualEntry.value.pu}
                                disabled
                                title='Calculado automaticamente: Quantidade x PU - não editável'
                                fractionDigits={2}
                            />
                            {requiresPuDegassed(manualEntry.value.product) && (
                                <CurrencyInput
                                    id='manual-received-value'
                                    label='Valor Recebido (R$)'
                                    value={Math.round(manualEntry.value.quantity) * manualEntry.value.puDegassed}
                                    disabled
                                    title='Calculado automaticamente: Quantidade x PU Desagiado - não editável'
                                    fractionDigits={2}
                                />
                            )}
                            {requiresPuDegassed(manualEntry.value.product) && (
                                <CurrencyInput
                                    id='manual-implied-tax-gross'
                                    label='Taxa Cheia (calculada) (%)'
                                    value={manualImpliedTaxGross.value ?? 0}
                                    disabled
                                    title='Calculada a partir do PU Desagiado/Taxa Cliente/prazo informados - não editável. A corretora não informa taxa cheia na emissão manual.'
                                    fractionDigits={2}
                                />
                            )}
                            {manualEntry.value.rateType === 'POS' && (
                                <CurrencyInput
                                    id='manual-di-futuro'
                                    label='DI Futuro (%) - opcional'
                                    value={manualEntry.value.diFuturo}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, diFuturo: v }; }}
                                    fractionDigits={2}
                                />
                            )}
                            {manualEntry.value.product === 'DI' && (
                                <CurrencyInput
                                    id='manual-additional-spread'
                                    label='Spread Adicional (%) - opcional'
                                    value={manualEntry.value.additionalSpread}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, additionalSpread: v }; }}
                                    fractionDigits={2}
                                />
                            )}
                        </div>
                        <div>
                            <button
                                type='button'
                                onClick={addManualItem}
                                className='px-4 py-2 text-sm border border-gray-600 text-gray-600 rounded hover:bg-gray-100 dark:border-gray-400 dark:text-gray-400 dark:hover:bg-slate-700'
                            >
                                Adicionar emissão
                            </button>
                        </div>

                        {manualItems.value.length > 0 && (
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700'>
                                        <th className='pb-2'>Produto</th>
                                        <th className='pb-2'>Aplicação</th>
                                        <th className='pb-2'>Vencimento</th>
                                        <th className='pb-2'>Indexador</th>
                                        <th className='pb-2'>Taxa</th>
                                        <th className='pb-2 text-right'>Quantidade</th>
                                        <th className='pb-2 text-right'>PU</th>
                                        <th className='pb-2 text-right'>PU Desagiado</th>
                                        <th className='pb-2 text-right'>Valor</th>
                                        <th className='pb-2'></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {manualItems.value.map((item, idx) => (
                                        <tr key={idx} className='border-b border-gray-100 dark:border-slate-700'>
                                            <td className='py-2'>{item.product}</td>
                                            <td className='py-2'>{extractAndConvertFrom(item.issue_date)}</td>
                                            <td className='py-2'>{extractAndConvertFrom(item.due_date)}</td>
                                            <td className='py-2'>{item.rate_type === 'POS' ? '%CDI' : 'PRÉ'}</td>
                                            <td className='py-2'>{item.tax.toFixed(2)}%</td>
                                            <td className='py-2 text-right'>{item.quantity}</td>
                                            <td className='py-2 text-right'>{item.pu.toFixed(2)}</td>
                                            <td className='py-2 text-right'>{item.pu_degassed !== null ? item.pu_degassed.toFixed(PU_DEGASSED_FRACTION_DIGITS) : '-'}</td>
                                            <td className='py-2 text-right'>{item.financial_value.toFixed(2)}</td>
                                            <td className='py-2 text-right'>
                                                <button
                                                    type='button'
                                                    onClick={() => removeManualItem(idx)}
                                                    className='text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400'
                                                    title='Remover'
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </PageForm>

        <DuplicateIssuanceModal show={duplicates.modal.show} rows={duplicates.modal.rows} onDecision={duplicates.decide} />
        <Dialog
            id='pu-desagiado-divergence'
            show={divergenceModal.value.show}
            onClose={() => resolveDivergenceModal(false)}
            size='lg'
            header={divergenceModal.value.rows.some((r) => r.diverges) ? 'PU Desagiado divergente' : 'Confira a Taxa Cheia antes de salvar'}
            body={(
                <div className='p-4'>
                    {divergenceModal.value.rows.some((r) => r.diverges) && (
                        <div className='flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400 mb-3'>
                            <FaExclamationTriangle className='shrink-0' />
                            <span>{divergenceModal.value.rows.filter((r) => r.diverges).length} linha(s) com PU Desagiado fora do esperado pela fórmula:</span>
                        </div>
                    )}
                    <div className='overflow-x-auto border border-gray-200 dark:border-slate-700 rounded-lg'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='text-left text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-800'>
                                    <th className='py-2 px-3 font-medium'>Linha</th>
                                    <th className='py-2 px-3 font-medium text-right'>Taxa Cheia Informada</th>
                                    <th className='py-2 px-3 font-medium text-right'>Taxa Cheia Calculada</th>
                                    <th className='py-2 px-3 font-medium text-right'>Diferença PU Desagiado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {divergenceModal.value.rows.map((r) => (
                                    <tr
                                        key={r.index}
                                        className={`border-t border-gray-100 dark:border-slate-700 ${r.diverges ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                                    >
                                        <td className='py-2 px-3'>{r.index + 1}</td>
                                        <td className='py-2 px-3 text-right [font-variant-numeric:tabular-nums]'>
                                            {r.informedTaxGross !== null ? `${r.informedTaxGross.toFixed(2)}%` : '—'}
                                        </td>
                                        <td className='py-2 px-3 text-right [font-variant-numeric:tabular-nums]'>
                                            {r.calculatedTaxGross !== null ? `${r.calculatedTaxGross.toFixed(2)}%` : '—'}
                                        </td>
                                        <td className={`py-2 px-3 text-right [font-variant-numeric:tabular-nums] ${r.diverges ? 'font-medium text-amber-600 dark:text-amber-400' : ''}`}>
                                            {r.relativeDifference !== null ? `${(r.relativeDifference * 100).toFixed(3)}%` : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            footer={(
                <div className='flex gap-3'>
                    <button
                        type='button'
                        onClick={() => resolveDivergenceModal(false)}
                        className='px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors'
                    >
                        Cancelar
                    </button>
                    <button
                        type='button'
                        onClick={() => resolveDivergenceModal(true)}
                        className={`px-4 py-2 text-sm text-white rounded-lg transition-colors ${divergenceModal.value.rows.some((r) => r.diverges)
                            ? 'bg-amber-500 hover:bg-amber-600'
                            : 'bg-blue-500 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700'}`}
                    >
                        {divergenceModal.value.rows.some((r) => r.diverges) ? 'Enviar mesmo assim' : 'Confirmar e Salvar'}
                    </button>
                </div>
            )}
        />
        </>
    );
};

export default IssuanceFormPage;
